import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import { Button } from "components/core/Button";
import { UnitIcon } from "components/molecules";
import formatUSD from "utils/formatUSD";

import "./UnitCardV2.scss";

// Number of primary amenities to show before the "+ X Amenities" toggle
const PRIMARY_AMENITIES_COUNT = 5;

// ================= MAIN COMPONENT =================

const UnitCardV2 = ({
	unit,
	ctaText,
	ctaSubtext,
	onReserve,
	className,
	expanded,
	onToggleExpand,
	cardRef,
	style
}) => {
	const [localExpanded, setLocalExpanded] = useState(false);
	const showAllAmenities = onToggleExpand ? expanded : localExpanded;

	// Derive display data from unit
	const {
		sizeText = "",
		unitType,
		availableAmenities = [],
		special,
		lowestPrice,
		startingPrice,
		quantity,
		squareFootage
	} = unit;
	const displaySize = sizeText.replace(/['\s]/g, "");
	const primaryAmenities = availableAmenities.slice(0, PRIMARY_AMENITIES_COUNT);
	const additionalAmenities = availableAmenities.slice(PRIMARY_AMENITIES_COUNT);
	const pricePerSqFt = lowestPrice && squareFootage
		? parseFloat((lowestPrice / squareFootage).toFixed(2))
		: null;

	const showScarcity = quantity && quantity <= 3;
	const scarcityText = showScarcity ? `Only ${quantity} Left!` : null;
	const hasDiscount = startingPrice && startingPrice > lowestPrice;
	const additionalCount = additionalAmenities.length;

	const handleReserve = () => {
		if (typeof window !== "undefined") {
			sessionStorage.setItem(
				`scroll_position:${window.location.pathname}${window.location.search}`,
				window.scrollY
			);
		}
		onReserve(unit);
	};

	const handleAmenitiesToggle = useCallback(() => {
		if (onToggleExpand) {
			onToggleExpand(unit.id);
		} else {
			setLocalExpanded((prev) => !prev);
		}
	}, [onToggleExpand, unit.id]);

	return (
		<div className={classnames("unit-card-v2", className)} ref={cardRef} style={style}>

			{/* HEADER */}
			<div className="unit-header">
				<div className="unit-header-left">
					<p className="unit-size">{displaySize}</p>

					{pricePerSqFt && (
						<span className="price-per-sqft">
							{formatUSD(pricePerSqFt, { showDecimal: true })} / sqft
						</span>
					)}
				</div>

				<UnitIcon
					unitType={unitType}
					squareFootage={squareFootage}
					className="unit-icon"
				/>
			</div>
			<div className="unit-bottom">
				{/* FEATURES */}
				<div className="features-section">
					<div className="amenities-list">
						<div className="amenities-header">
							<span className="amenities-label">Features</span>
						</div>

						<div className="primary-items">
							{primaryAmenities.map((item, index) => (
								<React.Fragment key={item}>
									<span className="amenity-item">{item}</span>
									{index < primaryAmenities.length - 1 && (
										<span className="amenity-dot" />
									)}
								</React.Fragment>
							))}

							{additionalCount > 0 && (
								<>
									<span className="amenity-dot" />
									<button
										type="button"
										className="amenities-toggle"
										onClick={handleAmenitiesToggle}
									>
										<span className="amenities-toggle-text">
											+ {additionalCount} Amenities
										</span>
									</button>
								</>
							)}
						</div>

						{showAllAmenities && additionalCount > 0 && (
							<div className="additional-items">
								<div className="items-list">
									{additionalAmenities.map((item, index) => (
										<React.Fragment key={item}>
											<span className="additional-item">{item}</span>
											{index < additionalAmenities.length - 1 && (
												<span className="dot-small" />
											)}
										</React.Fragment>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				{/* PROMO */}
				{special && (
					<div className="promo-banner push-down">
						<p>{special}</p>
					</div>
				)}

				<div className={classnames("separator", { "push-down": !special })} />

				<div className="pricing-section">
					<div className="price-display">
						<span className="current-price">
							{formatUSD(lowestPrice, { showDecimal: true })}
						</span>

						{(hasDiscount || scarcityText) && (
							<div className="price-meta">
								{hasDiscount && (
									<span className="original-price">
										{formatUSD(startingPrice, { showDecimal: true })}
									</span>
								)}

								{scarcityText && (
									<span className="scarcity-text">{scarcityText}</span>
								)}
							</div>
						)}
					</div>
				</div>
				<div className="separator" />
				{/* CTA */}
				<div className="cta-section">
					<Button theme="primary" to={unit.url} onClick={handleReserve}>
						{ctaText}
					</Button>

					{ctaSubtext && (
						<span className="cta-subtext">{ctaSubtext}</span>
					)}
				</div>
			</div>

		</div>
	);
};

UnitCardV2.propTypes = {
	unit: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		url: PropTypes.string,
		sizeText: PropTypes.string,
		unitType: PropTypes.string,
		availableAmenities: PropTypes.arrayOf(PropTypes.string),
		special: PropTypes.string,
		lowestPrice: PropTypes.number,
		startingPrice: PropTypes.number,
		quantity: PropTypes.number,
		squareFootage: PropTypes.number
	}).isRequired,
	ctaText: PropTypes.string,
	ctaSubtext: PropTypes.string,
	onReserve: PropTypes.func,
	className: PropTypes.string,
	expanded: PropTypes.bool,
	onToggleExpand: PropTypes.func,
	cardRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
	style: PropTypes.object
};

UnitCardV2.defaultProps = {
	ctaText: "Reserve Now, Pay Later",
	ctaSubtext: "You will not be charged until move-in",
	onReserve: () => {},
	className: "",
	expanded: false,
	onToggleExpand: null,
	cardRef: null,
	style: null
};

export default UnitCardV2;
